import {
  apiUrl,
  TENANT_ID,
  BUSINESS_UNIT_ID,
  communicationApiUrl,
} from "@/config/api";

export type NotificationDetails = {
  title?: string;
  body?: string;
};

export type ApiNotificationItem = {
  uuid?: string;
  is_read?: number;
  notification_details?: NotificationDetails;
  created_at?: string;
  updated_at?: string;
};

export type NotificationsListPayload = {
  total?: number;
  page?: number;
  offset?: number;
  unread_count?: number;
  notifications?: ApiNotificationItem[];
};

export type NotificationsListResponse = {
  success?: boolean;
  message?: string;
  data?: NotificationsListPayload;
  /** Set when upstream returns an error body */
  statusCode?: number;
};

export type CreateTicketPayload = {
  name: string;
  email: string;
  phone: string;
  phoneCode: string;
  cityId: number | string;
  cityName: string;
  // about: string;
  description: string;
  howDidYouFindUs: number;
  latestOffer: boolean;
  termsAndConditions: boolean;
};

export type CreateTicketResponse = {
  success?: boolean;
  message?: string;
};

export type UpdateUserConsentPayload = {
  phone: string;
  preferences: {
    email: boolean;
    sms: boolean;
    whatsapp: boolean;
    push_notifications: boolean;
    location_access: boolean;
  };
};

export type UserConsentResponse = {
  success?: boolean;
  message?: string;
};

function communicationToken(): string {
  const raw = process.env.NEXT_PUBLIC_COMMUNICATION_TOKEN?.trim() ?? "";
  // Supports env values wrapped in single/double quotes.
  return raw.replace(/^['"]|['"]$/g, "");
}

function csHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    "x-tenant-id": TENANT_ID,
    "x-business-unit-id": BUSINESS_UNIT_ID,
  };
}

export async function fetchNotificationsList(params: {
  customerId: string;
  page: number;
  offset: number;
  languageCode: "en" | "ar";
}): Promise<NotificationsListResponse> {
  const q = new URLSearchParams({
    customer_id: params.customerId.trim(),
    page: String(Math.max(1, params.page)),
    offset: String(Math.max(1, Math.min(100, params.offset))),
    language_code: params.languageCode,
  });
  const res = await fetch(apiUrl(`/notifications?${q.toString()}`), {
    method: "GET",
    headers: csHeaders(),
    cache: "no-store",
  });
  const data = (await res.json()) as NotificationsListResponse & {
    message?: string;
  };
  if (!res.ok) {
    return {
      success: false,
      message: data.message ?? `Request failed (${res.status})`,
      statusCode: res.status,
    };
  }
  return data;
}

export async function patchNotificationMarkRead(body: {
  customer_id: string;
  notification_id: string;
}): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(apiUrl("/notifications/mark-read"), {
    method: "PATCH",
    headers: csHeaders(),
    body: JSON.stringify({
      customer_id: body.customer_id.trim(),
      notification_id: body.notification_id.trim(),
    }),
    cache: "no-store",
  });
  const data = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    message?: string;
  };
  return {
    success: res.ok && data.success !== false,
    message: data.message,
  };
}

export async function createTicket(payload: CreateTicketPayload): Promise<CreateTicketResponse> {
  const res = await fetch(apiUrl(`/tickets/create`), {
    method: "POST",
    headers: csHeaders(),
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function updateUserConsent(payload: UpdateUserConsentPayload): Promise<UserConsentResponse> {
  const token = communicationToken();
  const res = await fetch(communicationApiUrl(`/user-consent`), {
    method: "POST",
    headers: {
      ...csHeaders(),
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function fetchUserConsent(phone: string): Promise<UserConsentResponse | null> {
  const token = communicationToken();
  const consentUrl = communicationApiUrl(`/user-consent/${phone}`);
  const res = await fetch(consentUrl, {
    method: "GET",
    headers: {
      ...csHeaders(),
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return res.json();
}